import React, { useState } from 'react';
import { Col, Row, Button } from 'reactstrap';
import Flatpickr from "react-flatpickr";
import { French } from "flatpickr/dist/l10n/fr";
import "flatpickr/dist/flatpickr.min.css";
import "flatpickr/dist/themes/airbnb.css";

interface SectionProps {
    onDateChange?: (selectedDates: Date[], dateStr: string) => void;
    onApplyFilter?: (startDate: Date, endDate: Date) => void;
    rightClickBtn?: () => void;
}

const Section = ({ onDateChange, onApplyFilter, rightClickBtn }: SectionProps) => {
    // État local pour les dates sélectionnées (vide par défaut)
    const [selectedDates, setSelectedDates] = useState<Date[]>([]);
    
    // État pour activer/désactiver le bouton
    const [isPeriodValid, setIsPeriodValid] = useState<boolean>(false);

    // Gérer le changement de dates dans le calendrier
    const handleDateChange = (dates: Date[], dateStr: string) => {
        console.log('📅 Dates sélectionnées:', dates, dateStr);
        
        // Mettre à jour l'état local
        setSelectedDates(dates);
        
        // Vérifier si on a une période valide (2 dates)
        setIsPeriodValid(dates.length === 2);
        
        // Appeler la fonction parent si elle existe
        if (onDateChange) {
            onDateChange(dates, dateStr);
        }
    };

    // Appliquer le filtre
    const handleApplyFilter = () => {
        if (selectedDates.length === 2 && onApplyFilter) {
            console.log('🔍 Application du filtre:', {
                start: selectedDates[0],
                end: selectedDates[1]
            });
            onApplyFilter(selectedDates[0], selectedDates[1]);
        }
    };

    // Réinitialiser le filtre et vider l'affichage
    const handleResetFilter = () => {
        setSelectedDates([]);
        setIsPeriodValid(false);
        
        // Réinitialiser également l'affichage du Flatpickr
        const flatpickrInstance = (document.querySelector('.flatpickr-input') as any)?._flatpickr;
        if (flatpickrInstance) {
            flatpickrInstance.clear(); // Cette méthode vide l'affichage
        }
        
        if (onApplyFilter) {
            onApplyFilter(null as any, null as any);
        }
    };

    // Formater la date sans virgule
    const formatDateDisplay = () => {
        if (selectedDates.length === 2) {
            const start = selectedDates[0].toLocaleDateString('fr-FR', { 
                day: '2-digit', 
                month: 'long', 
                year: 'numeric' 
            }).replace(',', ''); // Supprime la virgule
            
            const end = selectedDates[1].toLocaleDateString('fr-FR', { 
                day: '2-digit', 
                month: 'long', 
                year: 'numeric' 
            }).replace(',', ''); // Supprime la virgule
            
            return `${start} - ${end}`;
        }
        return 'Sélectionner une période';
    };

    return (
        <React.Fragment>
            <Row className="mb-3 pb-1">
                <Col xs={12}>
                    <div className="d-flex align-items-lg-center flex-lg-row flex-column">
                        <div className="flex-grow-1">
                            <h4 className="fs-16 mb-1">Tableau de bord</h4>
                            <p className="text-muted mb-0">
                                Bienvenue sur votre espace 
                            </p>
                        </div>
                        <div className="mt-3 mt-lg-0">
                            <div className="d-flex gap-2">
                                {/* Calendrier unique */}
                                <div className="input-group" style={{ width: '280px' }}>
                                    <Flatpickr
                                        value={selectedDates}
                                        className="form-control border-0 dash-filter-picker shadow"
                                        options={{
                                            mode: "range",
                                            dateFormat: "d M Y", // Format sans virgule
                                            locale: French,
                                            maxDate: new Date(),
                                            showMonths: 1,
                                            animate: true,
                                            monthSelectorType: 'dropdown'
                                        }}
                                        onChange={handleDateChange}
                                        placeholder="Choisir une période"
                                    />
                                    <div className="input-group-text bg-primary border-primary text-white">
                                        <i className="ri-calendar-2-line"></i>
                                    </div>
                                </div>

                                {/* Boutons d'action */}
                                <Button 
                                    color="primary"
                                    disabled={!isPeriodValid}
                                    onClick={handleApplyFilter}
                                >
                                    <i className="ri-check-line me-1"></i>
                                    OK
                                </Button>
                                
                                <Button 
                                    color="light"
                                    onClick={handleResetFilter}
                                >
                                    <i className="ri-close-line"></i>
                                </Button>

                                <button 
                                    type="button" 
                                    className="btn btn-soft-success waves-effect waves-light" 
                                    onClick={rightClickBtn}
                                >
                                    <i className="ri-pulse-line"></i>
                                </button>
                            </div>
                            
                            {/* Affichage de la période avec format corrigé */}
                            {selectedDates.length === 2 && (
                                <div className="mt-2 text-muted small">
                                    <i className="ri-calendar-event-line me-1"></i>
                                    {formatDateDisplay()}
                                </div>
                            )}
                        </div>
                    </div>
                </Col>
            </Row>
        </React.Fragment>
    );
};

export default Section;